import Form from "@/shared/form/form";
import Input from "@/shared/form/input";
import ColorPicker from "@/shared/form/color-picker";
import Dictionary from "@/shared/form/dictionary";
import { LocalizedValue } from "@/shared/form/localized-value";
import { Fieldset } from "@/shared/form/fieldset";
import { SubmitButton, SubmitWrapper } from "@/shared/form/button";
import { CompanyDocumentFolderSelect } from "@/modules/documents/entities/company-document-folder-select";
import { CompanyStructureSelect } from "@/modules/common/entities/company-structure/select";
import { HierarchyElementType } from "gql/graphql";
import { useTranslations } from "use-intl";
import { FolderFieldsType } from "../model/types";
import { useCompanyDocumentFolderEntity } from "../model/use-company-document-folder-entity";
import { useSaveCompanyDocumentFolder } from "../model/use-save-company-document-folder";
import { useIsParentHidden } from "../model/use-is-parent-hidden";

export function SaveCompanyDocumentFolderForm({
  id,
  parentId,
  afterSubmit,
}: {
  id?: string;
  parentId?: string | null;
  afterSubmit?: () => void;
}) {
  const t = useTranslations("CompanyDocumentFolder");
  const tc = useTranslations("Common");

  const {
    entity,
    formKey,
    loading: entityLoading,
  } = useCompanyDocumentFolderEntity({ id, parentId });
  const { save, loading } = useSaveCompanyDocumentFolder({
    afterSubmit,
    initialParentId: entity.parentId,
  });
  const parentHidden = useIsParentHidden(entity.parentId);

  return (
    <Form
      key={formKey}
      layout="vertical"
      disabled={loading || entityLoading}
      onFinish={save}
      initialValues={entity}
    >
      <Form.Item<FolderFieldsType> name="id" hidden>
        <Input />
      </Form.Item>
      <LocalizedValue
        name="name"
        label={t("Folder name")}
        render={({ placeholder }) => <Input placeholder={placeholder} />}
        myLocaleRules={[{ required: true }]}
      />

      {parentHidden ? (
        <>
          <Form.Item<FolderFieldsType> name="parentId" hidden>
            <Input />
          </Form.Item>
          <Form.Item label={t("Parent folder")}>
            <Input value={tc("Hidden")} disabled />
          </Form.Item>
        </>
      ) : (
        <Form.Item<FolderFieldsType> name="parentId" label={t("Parent folder")}>
          <CompanyDocumentFolderSelect
            excludeFolderId={id}
            placeholder={t("Root folder")}
          />
        </Form.Item>
      )}

      <Form.Item<FolderFieldsType> name="color" label={t("Color")}>
        <ColorPicker />
      </Form.Item>

      <Fieldset title={t("Access settings")}>
        <Form.Item<FolderFieldsType> name="structureIds" label={t("Structure")}>
          <CompanyStructureSelect
            multiple
            types={[
              HierarchyElementType.Organization,
              HierarchyElementType.Position,
            ]}
          />
        </Form.Item>

        <Form.Item<FolderFieldsType> name="jobIds" label={t("Job")}>
          <Dictionary dictionary="Job" mode="multiple" allowClear />
        </Form.Item>
      </Fieldset>

      <SubmitWrapper>
        <SubmitButton loading={loading} />
      </SubmitWrapper>
    </Form>
  );
}
